import { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import CopyIcon from 'modules/v2/common/components/SvgIcon/CopyIcon';

import strings from '../../strings';

/**
 * Prominent, one-click-copyable order number. Shared by the order-detail page
 * (task 40) and the confirmation step (task 39) so the presentation stays
 * consistent. Uses the project's standard `react-copy-to-clipboard` + CopyIcon.
 */
const CopyableOrderId = ({ orderId, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef();

  // Clear the reset timer on unmount so a copy-then-navigate within 1.5s can't
  // setState on an unmounted component.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = () => {
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  if (!orderId) {
    return null;
  }

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid="mod-order-id"
    >
      <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        {strings.detail.orderIdLabel}
      </span>
      <span className="font-mono text-base font-semibold text-neutral-800">
        {orderId}
      </span>
      <CopyToClipboard text={orderId} onCopy={handleCopy}>
        <button
          type="button"
          aria-label={strings.detail.copyOrderId}
          data-testid="mod-copy-order-id"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-500 hover:bg-neutral-50"
        >
          <CopyIcon />
          {copied ? strings.detail.copied : strings.detail.copyOrderId}
        </button>
      </CopyToClipboard>
    </div>
  );
};

export default CopyableOrderId;
